import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios'
import getAxiosInstance, { ServiceModuleValue } from '../utils/api-client'
import { useApiContext } from '../context/ApiContext'

export type ApiMethod = 'get' | 'post' | 'put' | 'patch' | 'delete'

export interface ApiRequestConfig extends Omit<AxiosRequestConfig, 'url' | 'method' | 'data'> {
    service: ServiceModuleValue
    organization?: string
    skipAuth?: boolean
    notifyOnError?: boolean
    customErrorHandler?: (error: any) => void
    [key: string]: any
}

export interface ApiHooks<TData = any, TError = any> {
    onBefore?: () => void
    onSuccess?: (data: TData, response?: AxiosResponse<TData>) => void
    onError?: (error: TError) => void
    onSettled?: () => void
}

export interface UseApiQueryOptions<TData = any, TError = any> extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
    queryKey: string | string[] // Now required instead of optional
}

export interface UseApiMutationOptions<TData = any, TError = any, TVariables = any>
    extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
    hooks?: ApiHooks<TData, TError>
    /** Reset mutation state when component unmounts. Useful for dialogs/modals. Defaults to false. */
    resetOnUnmount?: boolean
}

// New merged interface for mutation methods
export interface MergedMutationOptions<TData = any, TError = any, TVariables = any>
    extends Partial<ApiRequestConfig>, UseApiMutationOptions<TData, TError, TVariables> {
    // This interface combines both config and options
}

// New merged interface for query methods
export interface MergedQueryOptions<TData = any, TError = any>
    extends Partial<ApiRequestConfig>, UseApiQueryOptions<TData, TError> {
    // This interface combines both config and options for queries
}

interface ApiResponse<T = any> {
    data: T
    status: number
    statusText: string
    headers: any
}

const defaultRequestConfig: Partial<ApiRequestConfig> = {
    notifyOnError: true
}

const report = (error: unknown) => {
    console.error('API Error:', error)
}

const notify = (status: number | string, message: string) => {
    console.log(`Notification [${status}]: ${message}`);
}

// Partial, not ApiRequestConfig: callers pass `mergedConfig`, whose `service` widens to
// `string | undefined` via the spread of the Partial defaults. Nothing here reads `service`.
const handleApiError = (error: AxiosError | Error, config?: Partial<ApiRequestConfig>) => {
    const axiosError = error as AxiosError
    const status = axiosError?.response?.status
    const responseData = axiosError?.response?.data as any

    if (config?.customErrorHandler) {
        config.customErrorHandler(error)
        return
    }

    if (!config?.notifyOnError) {
        return
    }

    const ignoreStatuses = [401, 422, 423]

    if (status && ignoreStatuses.includes(status)) {
        if (status === 401) {
            notify(401, 'Not authenticated')
        }
        return
    }

    if (status === 413) {
        notify(413, 'The uploaded content is too large.')
        report(error)
        return
    }

    const message = status === 403
        ? 'Access Forbidden'
        : responseData?.message || 'Something went wrong'

    notify(status || 'Error', message)
    report(error)
}

const createAxiosRequest = async <T = any>(
    url: string,
    method: ApiMethod,
    data?: any,
    config?: ApiRequestConfig,
    apiConfig?: any
): Promise<ApiResponse<T>> => {
    const mergedConfig = { ...defaultRequestConfig, ...config }
    const { service, organization, skipAuth, ...axiosConfig } = mergedConfig

    const axiosInstance = getAxiosInstance({
        service: service!,
        organization: organization || apiConfig?.defaultOrganization,
        apiConfig
    })

    const requestConfig: AxiosRequestConfig = {
        ...axiosConfig,
        url,
        method,
        ...(data && method !== 'get' ? { data } : {}),
        ...(data && method === 'get' ? { params: data } : {})
    }

    if (skipAuth) {
        delete requestConfig.headers?.['X-XSRF-TOKEN']
        requestConfig.withCredentials = false
    }

    try {
        const response = await axiosInstance.request<T>(requestConfig)
        return {
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        }
    } catch (error) {
        handleApiError(error as AxiosError, mergedConfig)
        throw error
    }
}

export const useApiQuery = <TData = any, TError = any>(
    url: string,
    config: ApiRequestConfig,
    options: UseApiQueryOptions<TData, TError>, // queryKey is now required
    apiConfig?: any
) => {
    return useQuery<TData, TError, TData>({
        ...options,
        queryKey: options.queryKey,
        queryFn: async () => {
            const response = await createAxiosRequest<TData>(url, 'get', undefined, config, apiConfig)
            return response.data
        },
    })
}

export const useApiMutation = <TData = any, TError = any, TVariables = any>(
    url: string | ((variables: TVariables) => string),
    method: ApiMethod,
    config: ApiRequestConfig,
    options?: UseApiMutationOptions<TData, TError, TVariables>,
    apiConfig?: any
) => {
    const queryClient = useQueryClient()
    const { hooks, resetOnUnmount, ...mutationOptions } = options || {}

    const mutation = useMutation<TData, TError, TVariables>({
        mutationFn: async (variables: TVariables) => {
            hooks?.onBefore?.()

            const finalUrl = typeof url === 'function' ? url(variables) : url
            const response = await createAxiosRequest<TData>(
                finalUrl,
                method,
                variables,
                config,
                apiConfig
            )

            return response.data
        },
        onSuccess: (data, variables, context) => {
            hooks?.onSuccess?.(data)
            mutationOptions?.onSuccess?.(data, variables, context)
        },
        onError: (error, variables, context) => {
            hooks?.onError?.(error)
            mutationOptions?.onError?.(error, variables, context)
        },
        onSettled: (data, error, variables, context) => {
            hooks?.onSettled?.()
            mutationOptions?.onSettled?.(data, error, variables, context)
        },
        ...mutationOptions
    })

    useEffect(() => {
        if (!resetOnUnmount) return
        return () => {
            mutation.reset()
        }
    }, [resetOnUnmount])

    return mutation
}

// Helper function to separate config and options from merged parameter for mutations
const separateMergedMutationOptions = <TData = any, TError = any, TVariables = any>(
    mergedOptions?: MergedMutationOptions<TData, TError, TVariables>
): { config: Partial<ApiRequestConfig>, options: UseApiMutationOptions<TData, TError, TVariables> } => {
    if (!mergedOptions) {
        return { config: {}, options: {} }
    }

    const {
        // ApiRequestConfig properties
        service,
        organization,
        skipAuth,
        notifyOnError,
        customErrorHandler,
        headers,
        params,
        timeout,
        responseType,
        ...mutationOptions
    } = mergedOptions

    const config: Partial<ApiRequestConfig> = {
        ...(service && { service }),
        ...(organization && { organization }),
        ...(skipAuth !== undefined && { skipAuth }),
        ...(notifyOnError !== undefined && { notifyOnError }),
        ...(customErrorHandler && { customErrorHandler }),
        ...(headers && { headers }),
        ...(params && { params }),
        ...(timeout && { timeout }),
        ...(responseType && { responseType }),
    }

    return { config, options: mutationOptions }
}

// Helper function to separate config and options from merged parameter for queries
const separateMergedQueryOptions = <TData = any, TError = any>(
    mergedOptions?: MergedQueryOptions<TData, TError>
): { config: Partial<ApiRequestConfig>, options: UseApiQueryOptions<TData, TError> } => {
    if (!mergedOptions) {
        return { config: {}, options: {} as UseApiQueryOptions<TData, TError> }
    }

    const {
        // ApiRequestConfig properties
        service,
        organization,
        skipAuth,
        notifyOnError,
        customErrorHandler,
        headers,
        params,
        timeout,
        responseType,
        ...queryOptions
    } = mergedOptions

    const config: Partial<ApiRequestConfig> = {
        ...(service && { service }),
        ...(organization && { organization }),
        ...(skipAuth !== undefined && { skipAuth }),
        ...(notifyOnError !== undefined && { notifyOnError }),
        ...(customErrorHandler && { customErrorHandler }),
        ...(headers && { headers }),
        ...(params && { params }),
        ...(timeout && { timeout }),
        ...(responseType && { responseType }),
    }

    return { config, options: queryOptions }
}

export const useAPI = (defaultConfig?: Partial<ApiRequestConfig>) => {
    const { config: apiConfig } = useApiContext()
    const queryClient = useQueryClient()
    const mergedDefaultConfig = { ...defaultConfig }

    const createQueryWrapper = <TData = any, TError = any>(
        url: string,
        mergedOptions: MergedQueryOptions<TData, TError> // Now uses merged options
    ) => {
        const { config, options } = separateMergedQueryOptions(mergedOptions)
        const finalConfig = { ...mergedDefaultConfig, ...config } as ApiRequestConfig
        return useApiQuery<TData, TError>(url, finalConfig, options, apiConfig)
    }

    const createMutationWrapper = <TData = any, TError = any, TVariables = any>(
        url: string | ((variables: TVariables) => string),
        method: ApiMethod,
        mergedOptions?: MergedMutationOptions<TData, TError, TVariables>
    ) => {
        const { config, options } = separateMergedMutationOptions(mergedOptions)
        const finalConfig = { ...mergedDefaultConfig, ...config } as ApiRequestConfig
        return useApiMutation<TData, TError, TVariables>(url, method, finalConfig, options, apiConfig)
    }

    const get = <TData = any, TError = any>(
        url: string,
        options: MergedQueryOptions<TData, TError> // Now uses merged options
    ) => {

        return createQueryWrapper<TData, TError>(url, options)
    }

    const post = <TData = any, TError = any, TVariables = any>(
        url: string | ((variables: TVariables) => string),
        options?: MergedMutationOptions<TData, TError, TVariables>
    ) => createMutationWrapper<TData, TError, TVariables>(url, 'post', options)

    const put = <TData = any, TError = any, TVariables = any>(
        url: string | ((variables: TVariables) => string),
        options?: MergedMutationOptions<TData, TError, TVariables>
    ) => createMutationWrapper<TData, TError, TVariables>(url, 'put', options)

    const patch = <TData = any, TError = any, TVariables = any>(
        url: string | ((variables: TVariables) => string),
        options?: MergedMutationOptions<TData, TError, TVariables>
    ) => createMutationWrapper<TData, TError, TVariables>(url, 'patch', options)

    const deleteRequest = <TData = any, TError = any, TVariables = any>(
        url: string | ((variables: TVariables) => string),
        options?: MergedMutationOptions<TData, TError, TVariables>
    ) => createMutationWrapper<TData, TError, TVariables>(url, 'delete', options)

    const invalidateQueries = (queryKey?: any[]) => {
        if (queryKey) {
            return queryClient.invalidateQueries({ queryKey })
        }
        return queryClient.invalidateQueries()
    }

    const setQueryData = <TData = any>(queryKey: any[], data: TData) => {
        return queryClient.setQueryData(queryKey, data)
    }

    const getQueryData = <TData = any>(queryKey: any[]): TData | undefined => {
        return queryClient.getQueryData<TData>(queryKey)
    }

    const request = async <TData = any>(
        url: string, { method, data, config }
            : {
                method: ApiMethod,
                data?: any,
                config?: Partial<ApiRequestConfig>
            }): Promise<ApiResponse<TData>> => {
        const finalConfig = { ...mergedDefaultConfig, ...config } as ApiRequestConfig
        const response = await createAxiosRequest<TData>(url, method, data, finalConfig, apiConfig)
        return response
    }
    const fetcher = async <T = any>(url: string, config: Partial<ApiRequestConfig> & { "queryKey": string[] }): Promise<T> => {
        const finalConfig = { ...mergedDefaultConfig, ...config } as ApiRequestConfig
        const response = await createAxiosRequest<T>(url, 'get', undefined, finalConfig, apiConfig)
        return response.data
    }

    return {
        get,
        post,
        put,
        patch,
        delete: deleteRequest,
        invalidateQueries,
        setQueryData,
        getQueryData,
        queryClient,
        request,
        fetcher
    }
}